import { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DangerZone = ({ user }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  
  // For Clear Employee Records feature
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employeeConfirmText, setEmployeeConfirmText] = useState('');
  const [employeeLoading, setEmployeeLoading] = useState(false);
  
  // For Reset Clock Records feature
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // Fetch all employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/employees/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEmployees(data);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    
    if (user?.email === 'tech@reuseabox.co.uk') {
      fetchEmployees();
    }
  }, [user]);

  // Only show to tech@reuseabox.co.uk
  if (user?.email !== 'tech@reuseabox.co.uk') {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-gray-400" />
          <h3 className="text-xl font-bold text-gray-900">Danger Zone</h3>
        </div>
        <p className="text-gray-600">This section is restricted to master administrators only.</p>
      </div>
    );
  }

  const handleClearEmployeeRecords = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    
    if (employeeConfirmText !== 'CLEAR RECORDS') {
      toast.error('Please type the confirmation text exactly');
      return;
    }

    setEmployeeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/admin/clear-employee-records?employee_id=${selectedEmployee}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (res.ok) {
        const data = await res.json();
        toast.success(`All records cleared for ${data.employee.name}!`);
        setShowEmployeeDialog(false);
        setSelectedEmployee('');
        setEmployeeConfirmText('');
        
        // Show detailed results
        console.log('Cleared records:', data.cleared_counts);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to clear employee records');
      }
    } catch (error) {
      console.error('Error clearing employee records:', error);
      toast.error('Failed to clear employee records. Check console for details.');
    } finally {
      setEmployeeLoading(false);
    }
  };

  const handleClearAllData = async () => {
    if (confirmText !== 'DELETE ALL DATA') {
      toast.error('Please type the confirmation text exactly');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/clear-all-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('All data cleared successfully! Only master admin remains.');
        setShowConfirmDialog(false);
        setConfirmText('');
        
        // Show detailed results
        console.log('Cleared data counts:', data.cleared_counts);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to clear data');
      }
    } catch (error) {
      console.error('Error clearing data:', error);
      toast.error('Failed to clear data. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetClockRecords = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    const employeeNames = employees
      .filter(e => selectedEmployees.includes(e.id))
      .map(e => `${e.first_name} ${e.last_name}`)
      .join(', ');

    if (!window.confirm(`Are you ABSOLUTELY SURE you want to reset clock records for:\n\n${employeeNames}\n\nThis will delete:\n- Their kiosk time logs\n- Their driver schedule clock times and logs\n- Their warehouse schedule clock times\n\nType "RESET CLOCKS" in the next prompt to confirm.`)) {
      return;
    }

    const confirmation = window.prompt('Type "RESET CLOCKS" to confirm:');
    if (confirmation !== 'RESET CLOCKS') {
      toast.error('Reset cancelled - confirmation text did not match');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/system/reset-clock-records`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employee_ids: selectedEmployees
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Successfully reset clock records for ${selectedEmployees.length} employee(s)! Deleted ${data.deleted_count} records.`);
        setSelectedEmployees([]); // Clear selection
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset clock records');
      }
    } catch (error) {
      console.error('Error resetting clock records:', error);
      toast.error('Failed to reset clock records');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        <AlertTriangle className="w-6 h-6 text-red-600" />
        <h3 className="text-xl font-bold text-gray-900">⚠️ Danger Zone</h3>
      </div>
      
      <div className="space-y-6">
        {/* Clear Employee Records Section */}
        <div className="border-2 border-orange-200 rounded-lg p-6 bg-orange-50">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <UserX className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-orange-900 mb-2">Clear Employee Records</h4>
              <p className="text-sm text-orange-800 mb-4">
                Clear all time off, sickness, and absence records for a specific employee. This will permanently delete:
              </p>
              <ul className="text-sm text-orange-800 mb-4 ml-4 list-disc space-y-1">
                <li>All time-off requests (annual leave, etc.)</li>
                <li>All sickness records and absence data</li>
                <li>All return-to-work records</li>
              </ul>
              <p className="text-sm text-green-800 mb-4 font-semibold">
                ✓ Employee profile and other data will remain intact
              </p>
              <div className="bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-md inline-block mb-4">
                ⚠️ THIS ACTION CANNOT BE UNDONE
              </div>
              <div>
                <Button
                  onClick={() => setShowEmployeeDialog(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                >
                  <UserX className="w-4 h-4 mr-2" />
                  Clear Employee Records
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Reset Clock Records Section */}
        <div className="border-2 border-yellow-200 rounded-lg p-6 bg-yellow-50">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-yellow-900 mb-2">Reset Clock Records for Selected Employees</h4>
              <p className="text-sm text-yellow-800 mb-4">
                Select employees whose clock records you want to reset. This will delete:
              </p>
              <ul className="text-sm text-yellow-800 mb-4 ml-4 list-disc space-y-1">
                <li>Their kiosk time logs (clock-in/clock-out records)</li>
                <li>Their driver schedule clock times and log data</li>
                <li>Their warehouse schedule clock times</li>
              </ul>
              <p className="text-sm text-green-800 mb-4 font-semibold">
                ✓ Schedules and other data will remain intact
              </p>

              {/* Employee Selection */}
              <div className="bg-white rounded-lg p-4 mb-4 max-h-64 overflow-y-auto border border-yellow-300">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">
                    Select Employees ({selectedEmployees.length} selected)
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedEmployees(employees.map(e => e.id))}
                      className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedEmployees([])}
                      className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                {employees.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading employees...</p>
                ) : (
                  <div className="space-y-2">
                    {employees.map(emp => (
                      <label key={emp.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(emp.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployees([...selectedEmployees, emp.id]);
                            } else {
                              setSelectedEmployees(selectedEmployees.filter(id => id !== emp.id));
                            }
                          }}
                          className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-700">
                          {emp.first_name} {emp.last_name} ({emp.department})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-yellow-600 text-white text-xs font-bold px-3 py-1.5 rounded-md inline-block mb-4">
                ⚠️ THIS ACTION CANNOT BE UNDONE
              </div>
              <div>
                <Button
                  onClick={handleResetClockRecords}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
                  disabled={loading || selectedEmployees.length === 0}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Reset Clock Records ({selectedEmployees.length} employees)
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Clear All Data Section */}
        <div className="border-2 border-red-200 rounded-lg p-6 bg-red-50">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-red-900 mb-2">Clear All Data</h4>
            <p className="text-sm text-red-800 mb-4">
              This will permanently delete the following data:
            </p>
            <ul className="text-sm text-red-800 mb-4 ml-4 list-disc space-y-1">
              <li>All employees (except tech@reuseabox.co.uk)</li>
              <li>All time logs and clock-in/out records</li>
              <li>All time-off requests and absences</li>
              <li>All driver logs and schedules</li>
              <li>All timesheets and entries</li>
              <li>All surveys, newsletters, announcements</li>
              <li>All tasks and task completions</li>
            </ul>
            <p className="text-sm text-green-800 mb-4 font-semibold">
              ✓ The following will be PRESERVED:
            </p>
            <ul className="text-sm text-green-800 mb-4 ml-4 list-disc space-y-1">
              <li>All vehicles</li>
              <li>Impact stats and impact page content</li>
              <li>2026 Roadmap content</li>
            </ul>
            <div className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-md inline-block mb-4">
              ⚠️ THIS ACTION CANNOT BE UNDONE
            </div>
            <div>
              <Button
                onClick={() => setShowConfirmDialog(true)}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Data
              </Button>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Dialogs */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Confirm Data Deletion
            </DialogTitle>
            <DialogDescription>
              This action will permanently delete all data from the system. This cannot be undone!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-900 font-medium mb-2">
                  The following will be DELETED:
                </p>
                <ul className="text-xs text-red-800 space-y-1 ml-4 list-disc">
                  <li>All employees except tech@reuseabox.co.uk</li>
                  <li>All time tracking data</li>
                  <li>All driver logs and schedules</li>
                  <li>All timesheets</li>
                  <li>All content (surveys, newsletters, announcements, tasks)</li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-900 font-medium mb-2">
                  The following will be PRESERVED:
                </p>
                <ul className="text-xs text-green-800 space-y-1 ml-4 list-disc">
                  <li>All vehicles (fleet data)</li>
                  <li>Impact stats and impact page content</li>
                  <li>2026 Roadmap content</li>
                </ul>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">
                Type <span className="text-red-600 font-mono">DELETE ALL DATA</span> to confirm:
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE ALL DATA"
                className="mt-2 font-mono"
                disabled={loading}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false);
                setConfirmText('');
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleClearAllData}
              disabled={confirmText !== 'DELETE ALL DATA' || loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Deleting...' : 'Delete All Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Employee Records Dialog */}
      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <UserX className="w-6 h-6" />
              Clear Employee Records
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all time off, sickness, and absence records for the selected employee.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-semibold mb-2 block">
                  Select Employee:
                </Label>
                <Select 
                  value={selectedEmployee} 
                  onValueChange={setSelectedEmployee}
                  disabled={employeeLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose an employee..." />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-900 font-medium mb-2">
                  The following will be DELETED for this employee:
                </p>
                <ul className="text-xs text-orange-800 space-y-1 ml-4 list-disc">
                  <li>All time-off requests</li>
                  <li>All sickness records</li>
                  <li>All absence data</li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-900 font-medium mb-2">
                  The following will be PRESERVED:
                </p>
                <ul className="text-xs text-green-800 space-y-1 ml-4 list-disc">
                  <li>Employee profile</li>
                  <li>Login credentials</li>
                  <li>Role and permissions</li>
                </ul>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">
                Type <span className="text-orange-600 font-mono">CLEAR RECORDS</span> to confirm:
              </Label>
              <Input
                value={employeeConfirmText}
                onChange={(e) => setEmployeeConfirmText(e.target.value)}
                placeholder="CLEAR RECORDS"
                className="mt-2 font-mono"
                disabled={employeeLoading}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEmployeeDialog(false);
                setSelectedEmployee('');
                setEmployeeConfirmText('');
              }}
              disabled={employeeLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleClearEmployeeRecords}
              disabled={!selectedEmployee || employeeConfirmText !== 'CLEAR RECORDS' || employeeLoading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {employeeLoading ? 'Clearing...' : 'Clear Records'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DangerZone;
